import {Box, Paper, TableContainer, Typography} from "@mui/material";
import React, {useEffect, useState} from "react";
import User from "../../../model/User";
import {useResources} from "../../../stores/ResourceProvider";
import Account from "../../../model/Account";
import {ModelUserFilterInput, UserStatus} from "../../../API";
import {
  getISODateFromDate, humanizeString,
  isoToLocalDate,
  isoToLocalDateString,
} from "../../../stores/StoreUtilities";
import {globalColors} from "../../../styles/globalStyles";
import Chart from "react-apexcharts";
import LoadingProgress from "../../../components/LoadingProgress";
import {differenceInCalendarDays, addMinutes, addYears, addMonths, startOfMonth} from "date-fns";
import {
  DataGrid, GridColDef, useGridApiRef, GridToolbarContainer, GridToolbarExport, GridToolbarColumnsButton,
  GridToolbarQuickFilter, GridToolbarProps
} from "@mui/x-data-grid";
import {UserSettingType} from "../../../model/UserSetting";
import MonthYearPicker from "../../../components/form/MonthYearPicker";
import {deepClone} from "@mui/x-data-grid/internals";
import cloneDeep from "lodash.clonedeep";

const defaultOptions = {
  chart: {
    id: "signup-activity",
    type: 'bar',
    stacked: true,
    height: 350,
    width: "100%",
    events: {
      mounted: (chart: any) => {
        chart.windowResizeHandler();
      }
    },
    toolbar: {
      show: false,
      tools: {
        download: false
      }
    }
  },
  title: {
    text: "2025",
    align: "right",
    floating: true,
    margin: 0,
    style: {
      fontSize: "16px",
      fontFamily: "Poppins,sans-serif",
      color: "rgb(31, 92, 167)"
    }
  },
  dataLabels: {
    enabled: false
  },
  legend: {
    position: 'top', // TODO: Figure out why this isn't working
    horizontalAlign: 'left',
    onItemClick: {
      toggleDataSeries: false
    }
  },
  xaxis: {
    categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    axisBorder: {
      show: true
    }
  },
  yaxis: {
    // labels: {
    //   formatter: (value: number) => {
    //     return numberToMoneyFormat(value, 0)
    //   }
    // }
  },
  tooltip: {
    // y: {
    //   formatter: function (value: number) {
    //     return numberToMoneyFormat(value, 0)
    //   }
    // }
  },
  colors: [globalColors.categorical[0], globalColors.categorical[2], globalColors.categorical[3], globalColors.categorical[5], globalColors.categorical[8], globalColors.categorical[1], globalColors.categorical[6], globalColors.categorical[7], globalColors.categorical2[2]],
  responsive: [{
    breakpoint: undefined,
    options: {}
  }]
}

const today = new Date()

class UserActivity {
  id: string
  fullName: string
  startDate: Date
  trialEndDate?: Date
  quickStartStatus: string
  quickStartStep: string
  lastAccessDate: Date
  daysSinceStarted: number
  daysActive: number
  // timeActive: string
  daysInactive: number
  userStatus: UserStatus
  subscribedDate?: Date
  daysToConvert?: number
  cancelDate?: Date
  cancelReason?: string

  constructor (user: User) {
    this.id = user.id
    this.fullName = user.fullName
    this.startDate = isoToLocalDate(user.startDate)
    this.trialEndDate = user.trialEndDate ? isoToLocalDate(user.trialEndDate) : undefined
    const quickStartSetting = user.getUserSetting(UserSettingType.quickStart)
    this.quickStartStatus = quickStartSetting ? quickStartSetting.settings.status ?? "" : ""
    this.quickStartStep = ""
    if (quickStartSetting) {
      if (quickStartSetting.settings.status === "Completed") {
        this.quickStartStep = "Completed"
      } else {
        this.quickStartStep = `${quickStartSetting.settings.status} (${humanizeString(quickStartSetting.settings.step)})`
      }
    }
    this.lastAccessDate = isoToLocalDate(user.lastAccess)
    this.daysSinceStarted = differenceInCalendarDays(today, this.startDate)
    this.daysActive = user.sessionCount > 0 ? user.sessionCount : 1
    // const totalDuration = user.sessions.reduce((total, session) => total + session.duration, 0)
    // this.timeActive = `${Math.trunc(totalDuration / 3600)}:${Math.trunc((totalDuration % 3600) / 60)}`
    this.daysInactive = differenceInCalendarDays(today, this.lastAccessDate)
    this.userStatus = user.userStatus
    this.subscribedDate = user.subscribedOn ? isoToLocalDate(user.subscribedOn) : undefined
    this.daysToConvert = this.subscribedDate ? differenceInCalendarDays(this.subscribedDate, this.startDate) : undefined
    this.cancelDate = user.cancelledOn ? isoToLocalDate(user.cancelledOn) : user.deletedOn ? isoToLocalDate(user.deletedOn) : undefined
    this.cancelReason = user.deletedReason ?? ""
  }
}

interface CustomToolbarProps extends GridToolbarProps {
  startDate?: Date;
  onChangeStartDate?: (value: Date) => void;
}

const CustomToolbar = (props: CustomToolbarProps) => {
  const startDate = props.startDate ?? new Date()

  const handleChangeStartDate=(value: Date) => {
    if (props.onChangeStartDate) {
      props.onChangeStartDate(value)
    }
  }

  return (
    <GridToolbarContainer sx={{ display: 'flex', gap: 2, padding: 1, justifyContent: 'space-between' }}>
      <MonthYearPicker value={startDate}
                       onChange={(value: Date) => handleChangeStartDate(value)} />
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <GridToolbarQuickFilter />
        <GridToolbarColumnsButton />
        <GridToolbarExport />
      </Box>
    </GridToolbarContainer>
  );
};

const SignupActivityReport = ({width}: {width: number}) => {
  const [account, setAccount] = useState<Account | undefined>()
  const [users, setUsers] = useState<User[]>([])
  const [userActivity, setUserActivity] = useState<UserActivity[]>([])
  const [options, setOptions] = useState<any>()
  const [series, setSeries] = useState<any>()
  const [startDate, setStartDate] = useState<Date>(addMonths(startOfMonth(new Date()), -11))
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const {userStore, accountStore, notify} = useResources()
  const apiRef = useGridApiRef()

  useEffect(() => {
    if (userStore.isLoading === false && userStore.user && userStore.user.account) {
      setAccount(userStore.user.account)
      setUsers([])
      if (!isLoading) {
        loadUsers(userStore.user.account, startDate)
      }
    }
  }, [userStore.isLoading, startDate])

  useEffect(() => {
    // if (users.length > 0) {
      calculateData()
    // }
  }, [users])

  const monthLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

  const calculateData = () => {
    // if (!users) {
    //   return
    // }

    let activities = users.map((user: User) => new UserActivity(user))

    let signups = new Array<number>(12).fill(0)
    let activeQuickstarts = new Array<number>(12).fill(0)
    let pausedQuickstarts = new Array<number>(12).fill(0)
    let completedQuickstarts = new Array<number>(12).fill(0)
    let exitedQuickstarts = new Array<number>(12).fill(0)
    let activePostSignup = new Array<number>(12).fill(0)
    let activePostTrial = new Array<number>(12).fill(0)
    let subscriptions = new Array<number>(12).fill(0)
    let closures = new Array<number>(12).fill(0)

    const startMonth = startDate.getMonth()

    activities.forEach((activity: UserActivity) => {
      let signupMonth = activity.startDate.getMonth()
      if (signupMonth < startMonth) {
        signupMonth += 12
      }
      signupMonth -= startMonth

      signups[signupMonth]++
      if (activity.quickStartStatus) {
        if (activity.quickStartStatus === "Active") {
          activeQuickstarts[signupMonth]++
        } else if (activity.quickStartStatus === "Paused") {
          pausedQuickstarts[signupMonth]++
        } else if (activity.quickStartStatus === "Completed") {
          completedQuickstarts[signupMonth]++
        } else if (activity.quickStartStatus === "Exited") {
          exitedQuickstarts[signupMonth]++
        }
      }

      if (activity.lastAccessDate) {
        const days = differenceInCalendarDays(activity.lastAccessDate, activity.startDate)
        if (days > 0) {
          activePostSignup[signupMonth]++
        }
        if (days > 14) {
          activePostTrial[signupMonth]++
        }
      } else {
        console.debug(`No lastAccessDate`)
      }

      if (activity.subscribedDate) {
        // const subscribedMonth = activity.subscribedDate.getMonth()
        subscriptions[signupMonth]++
      }
      if (activity.cancelDate) {
        // const cancelledMonth = activity.cancelledDate.getMonth()
        closures[signupMonth]++
      }
    })

    setUserActivity(activities)

    const series = [
      {
        name: "Signups",
        group: "signups",
        data: signups
      },
      {
        name: "QuickStart Paused",
        group: "quickstart",
        data: pausedQuickstarts
      },
      {
        name: "QuickStart Exited",
        group: "quickstart",
        data: exitedQuickstarts
      },
      {
        name: "QuickStart Active",
        group: "quickstart",
        data: activeQuickstarts
      },
      {
        name: "QuickStart Completed",
        group: "quickstart",
        data: completedQuickstarts
      },
      {
        name: "Active Post Signup",
        group: "active",
        data: activePostSignup
      },
      {
        name: "Active Post Trial",
        group: "active",
        data: activePostTrial
      },
      {
        name: "Subscribed",
        group: "subscribed",
        data: subscriptions
      },
      {
        name: "Cancelled",
        group: "cancelled",
        data: closures
      }
    ]
    setSeries(series)

    const options = cloneDeep(defaultOptions)

    // Adjust xAxis categories, if not starting in Jan
    if (startMonth > 0) {
      const rotated = [...monthLabels.slice(startMonth), ...monthLabels.slice(0, startMonth)]
      options.xaxis.categories = rotated
    }
    setOptions(options)
  }

  const loadUsers = async (account: Account, startDate: Date) => {
    try {
      setIsLoading(true)
      let endDate = addMinutes(addYears(startDate, 1), -1)
      let filter : ModelUserFilterInput | undefined =
        {
          startDate: {between: [getISODateFromDate(startDate), getISODateFromDate(endDate)]}
        }
      let list = await accountStore.listUsersReportByAccount(account.id, filter)
      list.sort((a: User, b: User) => a.startDate.localeCompare(b.startDate))
      setUsers(list)
    } catch (err: any) {
      console.error("loadUsers error", err)
      notify.show("error", err.message)
    } finally {
      setIsLoading(false)
    }
  }

  const formatDate = (value?: string) => {
    return isoToLocalDateString(value, "MM/dd/yyyy")
  }

  const getRowId = (row: any) => {
    return row.id;
  }

  const columns : GridColDef[] = [
    {field: 'startDate', headerName: 'Start Date', width: 120, valueFormatter: (value) => formatDate(value)},
    {field: 'fullName', headerName: 'Full Name', width:200, resizable: true},
    {field: 'trialEndDate', headerName: 'Trial End', width: 120, valueFormatter: (value) => value ? formatDate(value) : ""},
    // {field: 'quickStartStatus', headerName: 'Quickstart', width: 120},
    {field: 'quickStartStep', headerName: 'Quickstart', width: 180},
    {field: 'daysActive', headerName: 'Days Active', width: 120},
    {field: 'lastAccessDate', headerName: 'Last Access', width: 120, valueFormatter: (value) => value ? formatDate(value) : ""},
    {field: 'daysInactive', headerName: 'Days Inactive', width: 120},
    // {field: 'timeActive', headerName: 'Active Time', width: 120},
    {field: 'userStatus', headerName: 'Status', width: 120},
    {field: 'subscribedDate', headerName: 'Subscribed', width: 120, valueFormatter: (value) => value ? formatDate(value) : ""},
    // {field: 'daysToConvert', headerName: 'Days To Subscribe', width: 120},
    {field: 'cancelDate', headerName: 'Cancelled', width: 120, valueFormatter: (value) => value ? formatDate(value) : ""},
    {field: 'cancelReason', headerName: 'Cancel Reason', width:200, resizable: true}
  ]

  const renderTable = () => {
    return (
      <DataGrid
        rows={userActivity}
        loading={isLoading}
        getRowId={getRowId}
        disableColumnFilter
        disableDensitySelector
        columns={columns}
        density="standard"
        apiRef={apiRef}
        initialState={{
          sorting: {
            sortModel: [{ field: 'startDate', sort: 'asc' }],
          },
        }}
        slots={{ toolbar: CustomToolbar }}
        slotProps={{
          toolbar: {
            startDate: startDate,
            onChangeStartDate: setStartDate
          } as any
        }}
        sx={{overflowX:"scroll"}}
      />
    )
  }

  if (isLoading) {
    return (
      <TableContainer component={Paper} sx={{paddingTop:1, overflowY:"hidden", minHeight:374, width:width-32}}>
        <Box display="flex" justifyContent="center" alignItems="center">
          <LoadingProgress marginTop="150px"/>
        </Box>
      </TableContainer>
    )
  }

  if (users && userActivity && series && options) {
    return (
      <Box display="flex" flexGrow={1} flexDirection="column" width={width-32} overflow="scroll">
        <TableContainer component={Paper} sx={{paddingTop:1, overflowY:"hidden", minHeight:"350px"}}>
          <Chart
            options={options}
            series={series}
            type="bar"
            height="350"
            width={`${width-32}px`}
          />
        </TableContainer>

        <Box display="flex" flexGrow={1} justifyContent="flex-start" maxHeight={40} mt={2} mb={1}>
          <Typography variant="h3" color="primary">Signup Activity</Typography>
        </Box>
        <TableContainer component={Paper} sx={{}}>
          <Box sx={{display:"flex", flexGrow:1, height:"calc(100vh - 240px)", maxHeight:"calc(100vh - 240px)", overflow:"scroll"}}>
            {renderTable()}
          </Box>
        </TableContainer>
      </Box>
    )
  } else {
    return null
  }
}

export default SignupActivityReport